/**
 * EATTA uses an April–March financial year.
 * e.g. April 2025 – March 2026 = "2025/2026"
 *
 * MEMBERSHIP YEAR DEFINITION
 * ══════════════════════════
 * A member's "membership year" is the calendar year their board approval
 * was recorded (boardApprovalDate), NOT their registration date or activeDate.
 * This is the date the board formally accepted them as a member.
 * Annual subscription renewals are due on the anniversary of that date.
 */

/** Return the financial-year string for a given calendar date */
export function toFinancialYear(date: Date): string {
  const fyStart = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  return `${fyStart}/${fyStart + 1}`;
}

/** Current financial year string */
export function currentFinancialYear(): string {
  return toFinancialYear(new Date());
}

/**
 * Generate a list of financial-year strings, newest first.
 * @param pastYears  how many past years to include (default 5)
 * @param futureYears how many future years to include (default 1)
 */
export function financialYearOptions(pastYears = 5, futureYears = 1): string[] {
  const now = new Date();
  const fyStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const years: string[] = [];
  for (let y = fyStart + futureYears; y >= fyStart - pastYears; y--) {
    years.push(`${y}/${y + 1}`);
  }
  return years;
}

/** Extract the 4-digit start year from "2025/2026" → 2025 */
export function fyStartYear(fy: string): number {
  return parseInt(fy.split("/")[0], 10);
}

/**
 * Compute renewal info based on the member's BOARD APPROVAL DATE.
 *
 * The board approval date is when the member was formally accepted —
 * their annual subscription renews on the anniversary of that date.
 *
 * @param boardApprovalDate  The date the board approved the member (YYYY-MM-DD).
 *                           Falls back to activeDate if not yet provided by backend.
 * @param renewalDueDateOverride  If the backend already computed renewalDueDate,
 *                                use it directly (avoids client-side date arithmetic).
 */
export function renewalInfo(
  boardApprovalDate?: string | null,
  renewalDueDateOverride?: string | null,
): {
  membershipYear: number | null;
  nextRenewal: Date | null;
  daysUntil: number | null;
  overdue: boolean;
} {
  const empty = { membershipYear: null, nextRenewal: null, daysUntil: null, overdue: false };

  // If backend already sent renewalDueDate, use it directly
  if (renewalDueDateOverride) {
    const override = new Date(renewalDueDateOverride);
    if (!isNaN(override.getTime())) {
      const daysUntil = Math.ceil((override.getTime() - Date.now()) / 86_400_000);
      const approvalYear = boardApprovalDate ? new Date(boardApprovalDate).getFullYear() : null;
      return {
        membershipYear: approvalYear,
        nextRenewal: override,
        daysUntil,
        overdue: daysUntil < 0,
      };
    }
  }

  if (!boardApprovalDate) return empty;
  const approved = new Date(boardApprovalDate);
  if (isNaN(approved.getTime())) return empty;

  const now = new Date();
  const membershipYear = approved.getFullYear();

  // Next annual anniversary of the board approval date
  let renewal = new Date(now.getFullYear(), approved.getMonth(), approved.getDate());
  if (renewal <= now) {
    renewal = new Date(now.getFullYear() + 1, approved.getMonth(), approved.getDate());
  }

  const daysUntil = Math.ceil((renewal.getTime() - now.getTime()) / 86_400_000);
  return { membershipYear, nextRenewal: renewal, daysUntil, overdue: daysUntil < 0 };
}

/**
 * Return the ISO date strings (YYYY-MM-DD) for the start and end of the
 * financial year that contains the given date (defaults to today).
 * EATTA FY runs April 1 → March 31.
 */
export function currentFYDates(date: Date = new Date()): { from: string; to: string } {
  const fyStart = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  return {
    from: `${fyStart}-04-01`,
    to: `${fyStart + 1}-03-31`,
  };
}

/** Format a Date or date-string as "DD MMM YYYY" */
export function fmtDate(d?: Date | string | null): string {
  if (!d) return "—";
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
